import { getDateDifference } from './date';
import { TaskStatus, TaskTitles } from './enum';

export const getAttribute = (attributes, type) => {
  const attribute =
    attributes?.find?.((attributeDetails) => attributeDetails.name === type)
      ?.value ?? '{}';
  try {
    return JSON.parse(attribute);
  } catch (e) {
    // Do nothing intentionally
  }
  return {};
};

const isLinkTask = (task) =>
  getAttribute(task?.attributes, 'meta')?.isLink &&
  task.taskTitle !== TaskTitles.clinicalStudiesReport;

const showTasksOnCoreQuestionnaireCondition = (task, data) => {
  const coreQuestionnaire = data.find(
    (task) => task?.taskTitle === TaskTitles.coreQuestionnaire
  );
  const vascularOutcomeGeneral = data.find(
    (task) => task?.taskTitle === TaskTitles.vascularOutcomesGeneral
  );
  const liverCyst = data.find(
    (task) => task?.taskTitle === TaskTitles.liverCyst
  );
  const healthCareAccess = data.find(
    (task) => task?.taskTitle === TaskTitles.healthCareAccess
  );
  const adpkdIS = data.find((task) => task?.taskTitle === TaskTitles.ADPKDIS);
  const adpkdPS = data.find((task) => task?.taskTitle === TaskTitles.ADPKDPDS);
  const vascularOutcomesLowerBody = data.find(
    (task) => task?.taskTitle === TaskTitles.vascularOutcomesLowerBody
  );
  const vascularOutcomesUpperBody = data.find(
    (task) => task?.taskTitle === TaskTitles.vascularOutcomesUpperBody
  );
  const vascularOutcomesFamily = data.find(
    (task) => task?.taskTitle === TaskTitles.vascularOutcomesFamily
  );

  const oldestCoreQuestionnaireDate =
    coreQuestionnaire?.oldestParticipantTaskEntry?.updatedAt;
  const coreQuestionnaireResponseDate = oldestCoreQuestionnaireDate
    ? getDateDifference(new Date(), oldestCoreQuestionnaireDate, 'month')
    : 0;

  const numberOfTimesCQCompleted = coreQuestionnaire?.quantitativeComplete;

  const coreQuestionnaireEntry =
    coreQuestionnaire?.latestParticipantTaskEntry?.data;
  const liverCystEntry = liverCyst?.latestParticipantTaskEntry?.data;
  const healthCareAccessEntry =
    healthCareAccess?.latestParticipantTaskEntry?.data;
  const adpkdISEntry = adpkdIS?.latestParticipantTaskEntry?.data;
  const adpkdPSEntry = adpkdPS?.latestParticipantTaskEntry?.data;
  const vascularOutcomeGeneralEntry =
    vascularOutcomeGeneral?.latestParticipantTaskEntry?.data;
  const vascularOutcomesLowerBodyEntry =
    vascularOutcomesLowerBody?.latestParticipantTaskEntry?.data;
  const vascularOutcomesUpperBodyEntry =
    vascularOutcomesUpperBody?.latestParticipantTaskEntry?.data;
  const vascularOutcomesFamilyEntry =
    vascularOutcomesFamily?.latestParticipantTaskEntry?.data;

  // Initial task condition
  const showInitialTasks =
    task.taskTitle === TaskTitles.coreQuestionnaire ||
    (Boolean(coreQuestionnaire?.latestParticipantTaskEntry) &&
      numberOfTimesCQCompleted >= 1 &&
      (task.taskTitle === TaskTitles.familyHistory ||
        task.taskTitle === TaskTitles.clinicalStudiesReport ||
        task.taskTitle === TaskTitles.dietLifestyle ||
        task.taskTitle === TaskTitles.socialDeterminants ||
        task.taskTitle === TaskTitles.patientCenteredSocialDeterminants));
  // Health care access condition
  const showHealthAccess =
    (task.taskTitle === TaskTitles.healthCareAccess &&
      numberOfTimesCQCompleted >= 1 &&
      coreQuestionnaireResponseDate >= 9) ||
    (task.taskTitle === TaskTitles.healthCareAccess &&
      Boolean(healthCareAccessEntry));

  // ADPKD-IS condition
  const showAdpkdIS =
    (task.taskTitle === TaskTitles.ADPKDIS &&
      numberOfTimesCQCompleted >= 1 &&
      coreQuestionnaireResponseDate >= 3) ||
    (task.taskTitle === TaskTitles.ADPKDIS && Boolean(adpkdISEntry));

  // ADPKD-PS condition
  const showAdpkdPS =
    (task.taskTitle === TaskTitles.ADPKDPDS &&
      numberOfTimesCQCompleted >= 1 &&
      coreQuestionnaireResponseDate >= 3) ||
    (task.taskTitle === TaskTitles.ADPKDPDS && Boolean(adpkdPSEntry));

  // Vascular outcome condition
  const showVascularOutcome =
    (task.taskTitle === TaskTitles.vascularOutcomesGeneral &&
      numberOfTimesCQCompleted >= 1 &&
      coreQuestionnaireResponseDate >= 6) ||
    (task.taskTitle === TaskTitles.vascularOutcomesGeneral &&
      Boolean(vascularOutcomeGeneralEntry));

  // Experience with Liver Cysts condition
  const hasLiverCyst =
    coreQuestionnaireEntry?.doYouHaveAnyCystsSelectAllThatApply?.liverCysts;
  const hasPolycysticLiver =
    coreQuestionnaireEntry
      ?.haveYouEverBeenToldByYourDoctorThatYouHaveSelectAllThatApply
      ?.polycysticLiverDiseasePld;
  const showLliverCyst =
    (task.taskTitle === TaskTitles.liverCyst &&
      coreQuestionnaireResponseDate >= 6 &&
      numberOfTimesCQCompleted >= 1 &&
      hasLiverCyst &&
      hasPolycysticLiver) ||
    (task.taskTitle === TaskTitles.liverCyst && Boolean(liverCystEntry));

  // Vascular Outcomes - Family - Aortic aneurysm of the abdomen/lower body condition
  const hasAneurysmOfLowerBody =
    vascularOutcomeGeneralEntry?.haveYouEverBeenDiagnosedWithAnAorticAneurysmOfTheAbdomenLowerBodyTheAortaIsTheLargeBloodVesselThatDeliversBloodFromTheHeartToTheRestOfTheBody?.toLowerCase() ===
    'yes';

  const showVascularOutcomeLowerBody =
    (task.taskTitle === TaskTitles.vascularOutcomesLowerBody &&
      coreQuestionnaireResponseDate >= 12 &&
      numberOfTimesCQCompleted >= 1 &&
      hasAneurysmOfLowerBody) ||
    (task.taskTitle === TaskTitles.vascularOutcomesLowerBody &&
      Boolean(vascularOutcomesLowerBodyEntry));

  // Vascular Outcomes - Family - Aortic aneurysm of the chest/upper body condition
  const hasAneurysmOfUpperBody =
    vascularOutcomeGeneralEntry?.haveYouEverBeenDiagnosedWithAnAorticAneurysmOfTheChestUpperBodyTheAortaIsTheLargeBloodVesselThatDeliversBloodFromTheHeartToTheRestOfTheBody?.toLowerCase() ===
    'yes';
  const showVascularOutcomeUpperBody =
    (task.taskTitle === TaskTitles.vascularOutcomesUpperBody &&
      coreQuestionnaireResponseDate >= 12 &&
      numberOfTimesCQCompleted >= 1 &&
      hasAneurysmOfUpperBody) ||
    (task.taskTitle === TaskTitles.vascularOutcomesUpperBody &&
      Boolean(vascularOutcomesUpperBodyEntry));

  // Vascular Outcomes - Family - Intracranial aneurysm condition
  const hasBrainAneurysm =
    vascularOutcomeGeneralEntry?.haveYouEverBeenDiagnosedWithAnIntracranialBrainAneurysm?.toLowerCase() ===
    'yes';
  const showVascularFamily =
    (task.taskTitle === TaskTitles.vascularOutcomesFamily &&
      coreQuestionnaireResponseDate >= 12 &&
      numberOfTimesCQCompleted >= 1 &&
      hasBrainAneurysm) ||
    (task.taskTitle === TaskTitles.vascularOutcomesFamily &&
      Boolean(vascularOutcomesFamilyEntry));

  return (
    showInitialTasks ||
    showVascularOutcomeLowerBody ||
    showVascularFamily ||
    showVascularOutcomeUpperBody ||
    showHealthAccess ||
    showVascularOutcome ||
    showAdpkdIS ||
    showAdpkdPS ||
    showLliverCyst
  );
};

export const filterParticipantTask = (data, task) => {
  return (
    task?.status !== TaskStatus.Incomplete ||
    isLinkTask(task) ||
    showTasksOnCoreQuestionnaireCondition(task, data)
  );
};
